// Generate real booking URLs for flights, hotels, and activities
import { addDaysIso, computeLegSchedule } from './legSchedule';

const encQ = encodeURIComponent;

// City name → IATA airport code
export const CITY_IATA = {
  lisbon: 'LIS', barcelona: 'BCN', paris: 'CDG', tokyo: 'NRT', rome: 'FCO',
  amsterdam: 'AMS', bali: 'DPS', bangkok: 'BKK', london: 'LHR', seoul: 'ICN',
  'hong kong': 'HKG', taipei: 'TPE', 'kuala lumpur': 'KUL', dubai: 'DXB',
  sydney: 'SYD', melbourne: 'MEL', maldives: 'MLE', prague: 'PRG',
  vienna: 'VIE', zurich: 'ZRH', osaka: 'KIX', kyoto: 'KIX',
  phuket: 'HKT', 'chiang mai': 'CNX', hanoi: 'HAN',
  'ho chi minh': 'SGN', saigon: 'SGN', jakarta: 'CGK',
  berlin: 'BER', munich: 'MUC', madrid: 'MAD', 'new york': 'JFK',
  ljubljana: 'LJU', zagreb: 'ZAG', budapest: 'BUD', istanbul: 'IST',
};

export const cityToIata = (destination) =>
  CITY_IATA[(destination || '').split(',')[0].trim().toLowerCase()] || null;

// Build Google Flights tfs parameter (base64 protobuf)
// Reverse-engineered field layout: field1=28, field2=tripType(1=one-way,2=round), field3=leg(repeated)
// Per leg: field2=date(str), field13={field1=1,field2=iata(str)}, field14={field1=1,field2=iata(str)}
// Trailing: field8=[0x01], field9=1, field14=1, field19=adults, field25=1
function buildTfs(origin, dest, departDate, returnDate, adults) {
  const s = (str) => str.split('').map(c => c.charCodeAt(0));
  const leg = (from, to, date) => [
    0x12, 0x0a, ...s(date),
    0x6a, 0x07, 0x08, 0x01, 0x12, 0x03, ...s(from),
    0x72, 0x07, 0x08, 0x01, 0x12, 0x03, ...s(to),
  ];
  const isRound = !!returnDate;
  const n = Math.max(1, Math.min(9, adults || 1));
  const bytes = [
    0x08, 0x1c, 0x10, isRound ? 0x02 : 0x01,
    0x1a, 0x1e, ...leg(origin, dest, departDate),
    ...(isRound ? [0x1a, 0x1e, ...leg(dest, origin, returnDate)] : []),
    0x42, 0x01, 0x01, 0x48, 0x01, 0x70, 0x01,
    0x98, 0x01, n,
    0xc8, 0x01, 0x01,
  ];
  return btoa(bytes.map(b => String.fromCharCode(b)).join(''));
}

export const googleFlightsUrl = (destination, dateStart, dateEnd, travelers = 2) => {
  const dest = destination.split(',')[0].trim();
  const iata = cityToIata(destination);
  if (iata && dateStart) {
    const tfs = buildTfs('SIN', iata, dateStart, dateEnd || null, travelers);
    return `https://www.google.com/travel/flights?tfs=${encQ(tfs)}&hl=en&gl=sg&curr=SGD`;
  }
  let url = `https://www.google.com/travel/flights?q=${encQ(`Flights from Singapore to ${dest}`)}&hl=en&gl=sg`;
  if (travelers > 1) url += `&adults=${travelers}`;
  return url;
};

// Like googleFlightsUrl but between two arbitrary cities (not fixed to a SIN
// origin) — used for inter-city legs on multi-city trips.
export const cityPairFlightsUrl = (fromCity, toCity, date, travelers = 2) => {
  const from = (fromCity || '').split(',')[0].trim();
  const to = (toCity || '').split(',')[0].trim();
  const fromIata = cityToIata(fromCity);
  const toIata = cityToIata(toCity);
  if (fromIata && toIata && date) {
    const tfs = buildTfs(fromIata, toIata, date, null, travelers);
    return `https://www.google.com/travel/flights?tfs=${encQ(tfs)}&hl=en&gl=sg&curr=SGD`;
  }
  let url = `https://www.google.com/travel/flights?q=${encQ(`Flights from ${from} to ${to}`)}&hl=en&gl=sg`;
  if (travelers > 1) url += `&adults=${travelers}`;
  return url;
};

// Rome2Rio covers trains/buses/ferries as well as flights — the better
// default for short inter-city hops (e.g. Tokyo → Osaka by rail).
export const rome2rioUrl = (fromCity, toCity) => {
  const from = (fromCity || '').split(',')[0].trim();
  const to = (toCity || '').split(',')[0].trim();
  return `https://www.rome2rio.com/s/${encQ(from)}/${encQ(to)}`;
};

export const kayakFlightsUrl = (destination, dateStart, dateEnd, travelers = 2) => {
  const dest = destination.split(',')[0].trim().toLowerCase().replace(/[^a-z0-9]/g, '');
  const d1 = dateStart || '';
  const d2 = dateEnd || '';
  return `https://www.kayak.com/flights/SIN-${encQ(dest)}/${d1}/${d2}?adults=${travelers}&currency=SGD`;
};

export const bookingDotComUrl = (destination, dateStart, dateEnd, adults = 2) => {
  const city = destination.split(',')[0].trim();
  return `https://www.booking.com/searchresults.html?ss=${encQ(city)}&checkin=${dateStart}&checkout=${dateEnd}&no_rooms=1&group_adults=${adults}`;
};

export const airbnbUrl = (destination, dateStart, dateEnd, adults = 2) => {
  const city = encQ(destination.split(',')[0].trim());
  return `https://www.airbnb.com/s/${city}/homes?checkin=${dateStart}&checkout=${dateEnd}&adults=${adults}`;
};

export const getYourGuideUrl = (query) =>
  `https://www.getyourguide.com/s/?q=${encQ(query)}&et=1`;

export const viatorUrl = (query) =>
  `https://www.viator.com/searchResults/all?text=${encQ(query)}`;

// Per-idea booking info — add any idea ID here to surface a "Book →" badge on the itinerary
export const IDEA_BOOKING = {
  i08: {
    needsBooking: true, estimatedCost: 15, currency: 'EUR',
    links: [
      { label: 'Official site', url: 'https://castelodesaojorge.pt/en/tickets/' },
      { label: 'GetYourGuide', url: 'https://www.getyourguide.com/lisbon-l42/castello-de-sao-jorge-t13040/' },
    ],
  },
  i09: {
    needsBooking: true, estimatedCost: 12, currency: 'EUR',
    links: [
      { label: 'Official site', url: 'https://jeronimosonline.dgpc.pt/en' },
      { label: 'GetYourGuide', url: 'https://www.getyourguide.com/lisbon-l42/jeronimos-monastery-skip-the-line-ticket-t59786/' },
    ],
  },
  i11: {
    needsBooking: true, estimatedCost: 8, currency: 'EUR',
    links: [
      { label: 'Official site', url: 'https://www.torrebelem.gov.pt/en/' },
      { label: 'GetYourGuide', url: 'https://www.getyourguide.com/lisbon-l42/torre-de-belem-t28820/' },
    ],
  },
  i13: {
    needsBooking: true, estimatedCost: 35, currency: 'EUR',
    links: [
      { label: 'Restaurant', url: 'https://www.mesadefrades.com/' },
      { label: 'TheFork', url: 'https://www.thefork.com/restaurant/mesa-de-frades-v543601' },
    ],
  },
  i14: {
    needsBooking: true, estimatedCost: 45, currency: 'EUR',
    links: [
      { label: 'GetYourGuide', url: 'https://www.getyourguide.com/lisbon-l42/tile-painting-workshop-t35617/' },
      { label: 'Viator', url: 'https://www.viator.com/searchResults/all?text=tile+painting+workshop+lisbon' },
    ],
  },
  i15: {
    needsBooking: true, estimatedCost: 55, currency: 'EUR',
    links: [
      { label: 'GetYourGuide', url: 'https://www.getyourguide.com/lisbon-l42/tagus-river-sunset-sailing-t35619/' },
      { label: 'Viator', url: 'https://www.viator.com/searchResults/all?text=tagus+sunset+sailing+lisbon' },
    ],
  },
  i16: {
    needsBooking: true, estimatedCost: 60, currency: 'EUR',
    links: [
      { label: 'GetYourGuide', url: 'https://www.getyourguide.com/costa-da-caparica-l97399/surf-lesson-t35612/' },
      { label: 'Viator', url: 'https://www.viator.com/searchResults/all?text=surf+lesson+caparica+lisbon' },
    ],
  },
  i17: {
    needsBooking: true, estimatedCost: 28, currency: 'EUR',
    links: [
      { label: 'Parques de Sintra', url: 'https://www.parquesdesintra.pt/en/parks-monuments/park-palace-of-pena/' },
      { label: 'GetYourGuide', url: 'https://www.getyourguide.com/sintra-l1003/pena-palace-ticket-t35614/' },
    ],
  },
  i04: {
    needsBooking: true, estimatedCost: 40, currency: 'EUR',
    links: [
      { label: 'Restaurant', url: 'https://www.acevicheria.pt/en/' },
    ],
  },
};


export function bookingInfoForIdea(idea, destination = '') {
  if (!idea) return null;
  if (IDEA_BOOKING[idea.id]) return IDEA_BOOKING[idea.id];
  if (!idea.requiresBooking && !idea.bookingQuery) return null;

  const city = (destination || '').split(',')[0].trim();
  const query = idea.bookingQuery || `${idea.title}${city ? ` ${city}` : ''}`;

  return {
    needsBooking: true,
    estimatedCost: idea.estimatedCost,
    currency: idea.currency || 'USD',
    description: idea.description,
    links: [
      { label: 'GetYourGuide', url: getYourGuideUrl(query) },
      { label: 'Viator', url: viatorUrl(query) },
    ],
  };
}

// Build the full booking checklist for a trip
// Per-leg check-in/check-out date ranges for multi-city trips, derived from
// each leg's dayCount cumulative offset from trip.dateStart. Returns null for
// single-destination trips (legs.length < 2) — shared by the transit-hop and
// per-leg-hotel logic below, and by ItineraryScreen's hotelByDay so both stay
// in sync on the same day-index math.
export function legDateRanges(trip) {
  const legs = trip?.legs || [];
  if (legs.length < 2) return null;
  const dateStart = trip?.dateStart || '';
  // dayStart honors overlapStart (a leg sharing its arrival day with the
  // previous leg's departure), so checkin/checkout dates stay in sync with
  // the itinerary's day-card assignment — see computeLegSchedule.
  const { offsets } = computeLegSchedule(legs);
  return legs.map((leg, i) => {
    const dayStart = offsets[i];
    const dayEnd = dayStart + (leg.dayCount || 0);
    return {
      destination: leg.destination || '',
      dayStart,
      dayEnd,
      dateStart: dateStart ? addDaysIso(dateStart, dayStart) : '',
      dateEnd: dateStart ? addDaysIso(dateStart, dayEnd) : '',
    };
  });
}

export function buildBookingChecklist(trip) {
  const { destination = '', dateStart = '', dateEnd = '', travelers = 2 } = trip;
  const city = destination.split(',')[0].trim();
  const items = [];
  const ranges = legDateRanges(trip);
  // For multi-city trips, the route needs every stop, not just the primary
  // destination — "SIN ↔ Barcelona" was silently dropping Brussels etc.
  const legCities = ranges ? ranges.map(r => (r.destination || '').split(',')[0].trim()).filter(Boolean) : [];
  const routeLabel = legCities.length > 1 ? `SIN → ${legCities.join(' → ')} → SIN` : `SIN ↔ ${city}`;

  const gfUrl = googleFlightsUrl(destination, dateStart, dateEnd, travelers);
  items.push({
    id: 'flights',
    kind: 'flight',
    title: 'Flights/Transport',
    detail: `${routeLabel}${dateStart ? ` · ${dateStart}${dateEnd ? ` → ${dateEnd}` : ''}` : ''} · ${travelers} pax`,
    links: [{ label: 'Google Flights', url: gfUrl }],
  });

  // One "Accommodation" item, like "Flights" — any number of stays live as
  // legs inside bookings.accommodation_details, always shown chronologically,
  // instead of a separate checklist item per city that fragmented the data
  // across hotel_<i>_details/accom_<i>_details/trip.accommodations.
  const accomLinks = ranges
    ? ranges.flatMap((r, i) => {
        const rCity = (r.destination || '').split(',')[0].trim();
        const stop = rCity || `Stop ${i + 1}`;
        return [
          { label: `Booking.com — ${stop}`, url: bookingDotComUrl(r.destination, r.dateStart, r.dateEnd, travelers) },
          { label: `Airbnb — ${stop}`, url: airbnbUrl(r.destination, r.dateStart, r.dateEnd, travelers) },
        ];
      })
    : [
        { label: 'Booking.com', url: bookingDotComUrl(destination, dateStart, dateEnd, travelers) },
        { label: 'Airbnb', url: airbnbUrl(destination, dateStart, dateEnd, travelers) },
        { label: 'Hotels.com', url: `https://www.hotels.com/search.do?q-destination=${encQ(city)}&q-check-in=${dateStart}&q-check-out=${dateEnd}&q-rooms=1&q-room-0-adults=${travelers}` },
      ];
  items.push({
    id: 'accommodation',
    kind: 'accommodation',
    title: 'Accommodation',
    detail: `${legCities.length > 1 ? legCities.join(', ') : city}${dateStart ? ` · ${dateStart}${dateEnd ? ` – ${dateEnd}` : ''}` : ''} · ${travelers} guests`,
    links: accomLinks,
  });

  // One "Activities" item, same pattern — keep uploading a booking for each
  // tour/ticket/reservation and they all live in one chronological-order
  // list. No discovery links (no GetYourGuide/Viator) — this is purely a
  // place to file bookings you already made, not a place to shop for new ones.
  items.push({
    id: 'activities',
    kind: 'activity',
    title: 'Activities',
    detail: `${legCities.length > 1 ? legCities.join(', ') : city} · ${travelers} travelers`,
    links: [],
  });

  items.push({
    id: 'insurance',
    kind: 'insurance',
    title: 'Travel Insurance',
    detail: 'Medical, cancellation, luggage coverage',
    links: [
      { label: 'MoneySmart', url: 'https://www.moneysmart.sg/travel-insurance' },
      { label: 'DirectAsia', url: 'https://www.directasia.com/travel-insurance/' },
      { label: 'FWD', url: 'https://www.fwd.com.sg/travel-insurance' },
      { label: 'NTUC Income', url: 'https://www.income.com.sg/travel-insurance' },
    ],
  });

  items.push({
    id: 'roaming',
    kind: 'roaming',
    title: 'Mobile Roaming',
    detail: 'Data & calls while abroad',
    links: [
      { label: 'Singtel Roaming', url: 'https://www.singtel.com/personal/products-services/mobile/international-travel' },
      { label: 'StarHub Roaming', url: 'https://www.starhub.com/personal/mobile/roaming.html' },
      { label: 'M1 Roaming', url: 'https://www.m1.com.sg/mobile/roaming' },
      { label: 'Airalo eSIM', url: 'https://www.airalo.com' },
    ],
  });

  return items;
}
